/**
 * Ghost Content API client (read-only, public key — safe in browser).
 *
 * Admin API calls (draft creation) go through the FastAPI backend so the
 * Admin key never touches the client.
 */

// Provided at build time via Vite env vars. The blog section degrades gracefully
// if unset — ghostConfigured will be false and all reads return empty.
const GHOST_URL = import.meta.env.VITE_GHOST_URL || ''
const CONTENT_KEY = import.meta.env.VITE_GHOST_CONTENT_KEY || ''

/** Blog is optional — true only when the Ghost URL + content key are configured. */
export const ghostConfigured = !!(GHOST_URL && CONTENT_KEY)

// The Ghost instance is shared across projects (VICTOR publishes there too).
// Every LifeOdds post carries the internal tag #lifeodds (slug: hash-lifeodds)
// — every read filters on it so other projects' posts never appear.
const SITE_TAG_FILTER = 'tag:hash-lifeodds'

export interface GhostTag {
  id: string
  name: string
  slug: string
}

export interface GhostPost {
  id: string
  title: string
  slug: string
  html: string | null
  excerpt: string | null
  feature_image: string | null
  published_at: string
  reading_time: number
  tags: GhostTag[]
}

export interface GhostPostsResponse {
  posts: GhostPost[]
  meta?: { pagination: { total: number; pages: number; page: number } }
}

function ghostUrl(path: string, params: Record<string, string> = {}): string {
  const url = new URL(`${GHOST_URL}/ghost/api/content/${path}`)
  url.searchParams.set('key', CONTENT_KEY)
  for (const [k, v] of Object.entries(params)) url.searchParams.set(k, v)
  return url.toString()
}

export async function getPosts(limit = 12, page = 1): Promise<GhostPostsResponse> {
  if (!ghostConfigured) return { posts: [], meta: { pagination: { total: 0, pages: 0, page: 1 } } }
  const res = await fetch(
    ghostUrl('posts/', {
      limit: String(limit),
      page: String(page),
      filter: SITE_TAG_FILTER,
      // `tags` is a relation, not a column — it belongs in `include` only.
      // Listing it in `fields` makes Ghost SELECT a non-existent column and 400.
      include: 'tags',
      fields: 'id,title,slug,excerpt,feature_image,published_at,reading_time',
      order: 'published_at desc',
    })
  )
  if (!res.ok) throw new Error(`Ghost API error: ${res.status}`)
  return res.json()
}

export async function getPost(slug: string): Promise<GhostPost | null> {
  if (!ghostConfigured) return null
  // Slug is interpolated into an NQL filter where "," is a low-precedence OR —
  // a crafted slug could disjunct past the site-tag scope. Ghost slugs are
  // strictly [a-z0-9-], so reject anything else before it reaches the query.
  if (!/^[a-z0-9-]+$/.test(slug)) return null
  // The /posts/slug/ endpoint can't be filtered, so use the list endpoint —
  // otherwise another project's post would render at /blog/<slug> here.
  const res = await fetch(
    ghostUrl('posts/', { filter: `slug:${slug}+${SITE_TAG_FILTER}`, include: 'tags' })
  )
  if (!res.ok) throw new Error(`Ghost API error: ${res.status}`)
  const data: GhostPostsResponse = await res.json()
  return data.posts?.[0] ?? null
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}
